import { ApiNote } from 'api/rtk-api'
import clsx from 'clsx'
import { Card, LabelValueRow } from 'components/core-v2'
import Highcharts from 'highcharts'
import HighchartsMore from 'highcharts/highcharts-more'
import HighchartsReact from 'highcharts-react-official'
import { useRef } from 'react'
import { displayPercentage } from 'utilities/number'

import classes from './classification-card.module.scss'

HighchartsMore(Highcharts)

const CapitalLabel = () => (
  <div className={classes.label}>
    <span className={clsx(classes.dot, classes.capital)} />
    Capital
  </div>
)
const YieldLabel = () => (
  <div className={classes.label}>
    <span className={clsx(classes.dot, classes.yield)} />
    Yield
  </div>
)

const GAUGE_THICKNESS = 55
const getOptions = (value: number): Highcharts.Options => ({
  chart: {
    type: 'gauge',
    plotBackgroundColor: null,
    plotBackgroundImage: null,
    plotBorderWidth: 0,
    plotShadow: false,
    width: 200,
    height: 132
  },
  title: null,
  tooltip: {
    enabled: false
  },
  pane: {
    startAngle: -90,
    endAngle: 90,
    background: null,
    center: ['50%', '92%'],
    size: '200px'
  },
  yAxis: {
    min: 0,
    max: 100,
    tickLength: GAUGE_THICKNESS,
    tickAmount: 1,
    tickWidth: 3,
    tickPositions: [value],
    minorTickInterval: null,
    labels: {
      enabled: false
    },
    plotBands: [
      {
        from: 0,
        to: value,
        color: '#d8737f',
        thickness: GAUGE_THICKNESS
      },
      { from: value, to: 100, color: '#d8737f', thickness: GAUGE_THICKNESS }
    ]
  },
  series: [
    {
      type: 'gauge',
      data: [value],
      dataLabels: {
        enabled: false
      },
      dial: {
        radius: '38px',
        baseWidth: 15,
        baseLength: '0',
        rearLength: '0',
        borderWidth: 0,
        topWidth: 0,
        backgroundColor: '#2e2424'
      },
      pivot: {
        backgroundColor: 'white',
        borderWidth: 5,
        borderColor: '#2e2424',
        radius: 5
      },
      label: {
        enabled: false
      }
    }
  ]
})

interface Props {
  note: ApiNote
}

export const ClassificationCard = ({ note }: Props) => {
  const ref = useRef()
  const noteValue = note.associatedTrades.reduce((sum, trade) => trade.accountValue + sum, 0)
  const yieldCapitalValues = note.associatedTrades.reduce(
    (acc, trade) => {
      const weight = trade.accountValue / noteValue
      acc.yieldValue += weight * trade.yieldPercentage
      acc.capitalValue += weight * trade.capitalPercentage
      return acc
    },
    { yieldValue: 0, capitalValue: 0 }
  )
  const total = yieldCapitalValues.yieldValue + yieldCapitalValues.capitalValue
  const yieldPercentage = (100 / total) * yieldCapitalValues.yieldValue
  const capitalPercentage = (100 / total) * yieldCapitalValues.capitalValue

  const noteType = note.taxClassification

  const options = getOptions(capitalPercentage)

  return (
    <Card.Wrapper className={classes.root}>
      <p>
        This is a <strong>{noteType}</strong> note. The aggregate weight of {noteType} assets should
        be greater than 50%.
      </p>
      <div className={classes.gaugeWrapper}>
        <HighchartsReact ref={ref} highcharts={Highcharts} options={options} />
        <div className={classes.tableWrapper}>
          <LabelValueRow
            label={<CapitalLabel />}
            value={displayPercentage(capitalPercentage / 100, 2)}
          />
          <LabelValueRow
            showBottomBorder
            label={<YieldLabel />}
            value={displayPercentage(yieldPercentage / 100, 2)}
          />
        </div>
      </div>
    </Card.Wrapper>
  )
}
